import apiService from "../shared/apiService";

export interface ExpectedMetric {
  metric_name: string;
  expected_value: string;
}

export interface RecommendedCampaign {
  channel: string;
  priority: number;
  campaign_type: string;
  main_message: string;
  metrics_expected: ExpectedMetric[];
  cta: string;
}

export interface BudgetAllocation {
  channel: string;
  allocation_percentage: number;
}

export interface RefinementChange {
  area: string;
  current: string;
  recommended: string;
  reason: string;
}

export interface RefinementData {
  performance_summary: string;
  what_is_working: string[];
  what_is_not_working: string[];
  recommended_changes: RefinementChange[];
  updated_budget_allocation: BudgetAllocation[];
}

export interface StrategyData {
  is_approved: boolean | null;
  overall_strategy_summary: string;
  recommended_campaigns: RecommendedCampaign[];
  suggested_budget_allocation: BudgetAllocation[] | Record<string, number>;
  next_steps: string[];
  campaign_objective?: string;
  session_id?: number;
  has_user_edits?: boolean;
  user_notes?: string;
  refinement?: RefinementData | null;
  organic_suggestions?: OrganicPostSuggestion[];
  weekly_budget?: number;
}

export interface OrganicPostSuggestion {
  topic: string;
  format: string;
  copy: string;
  hook: string;
  cta: string;
}

export interface OrganicStrategyData {
  suggestions: OrganicPostSuggestion[];
}

class AdsStrategyService {
  /**
   * Obtém a última estratégia de campanha do CMO AI
   */
  async getStrategy(): Promise<StrategyData> {
    return await apiService.get<StrategyData>("/api/agent/ads/strategy");
  }

  /**
   * Força a geração de uma nova estratégia de campanha usando o motor de IA
   */
  async recommendStrategy(
    modelName?: string,
    objective?: string,
  ): Promise<StrategyData> {
    return await apiService.post<StrategyData>(
      "/api/agent/ads/strategy/recommend",
      { model_name: modelName, objective },
    );
  }

  /**
   * Força a geração de uma nova estratégia cruzando dados do cliente e tendências da internet (Deep Research).
   */
  async deepResearchStrategy(
    modelName?: string,
    objective?: string,
  ): Promise<StrategyData> {
    return await apiService.post<StrategyData>(
      "/api/agent/ads/strategy/deep-research",
      { model_name: modelName, objective },
    );
  }

  /**
   * Sugere publicações orgânicas baseadas nas métricas.
   */
  async suggestOrganicPosts(
    modelName?: string,
    sessionId?: number,
  ): Promise<OrganicStrategyData> {
    return await apiService.post<OrganicStrategyData>(
      "/api/agent/ads/strategy/suggest-organic",
      { model_name: modelName, session_id: sessionId },
    );
  }

  /**
   * Salva edições manuais do usuário na estratégia atual.
   */
  async saveStrategy(
    sessionId: number | undefined,
    userEdits: Partial<StrategyData>,
    campaignObjective?: string,
  ): Promise<{ success: boolean; session_id: number; strategy: StrategyData }> {
    return await apiService.put("/api/agent/ads/strategy/save", {
      session_id: sessionId,
      user_edits: userEdits,
      campaign_objective: campaignObjective,
    });
  }

  /**
   * Salva edições avançadas de toda a estratégia.
   */
  async saveStrategyEdits(
    data: StrategyData,
  ): Promise<{ success: boolean; data: StrategyData }> {
    return await apiService.put<{ success: boolean; data: StrategyData }>(
      "/api/agent/ads/strategy/edit",
      data,
    );
  }

  /**
   * Pede ao agente para verificar resultados reais e recomendar modificações.
   */
  async refineStrategy(
    sessionId?: number,
    modelName?: string,
  ): Promise<{
    success: boolean;
    refinement: RefinementData;
    session_id: number;
  }> {
    return await apiService.post("/api/agent/ads/strategy/refine", {
      session_id: sessionId,
      model_name: modelName,
    });
  }

  /**
   * Envia feedback (like/dislike ou aprovação) para a estratégia atual.
   */
  async sendFeedback(
    isApproved: boolean,
  ): Promise<{ success: boolean; message: string }> {
    return await apiService.post<{ success: boolean; message: string }>(
      "/api/agent/ads/strategy/feedback",
      { is_approved: isApproved },
    );
  }

  /**
   * Envia o comando para o Backend acionar o microserviço autônomo.
   * O microserviço criará as campanhas no Meta e Google Ads em background.
   */
  async applyStrategy(
    sessionId?: number,
  ): Promise<{ success: boolean; message: string; results: any }> {
    return await apiService.post<{
      success: boolean;
      message: string;
      results: any;
    }>("/api/agent/ads/strategy/apply", { session_id: sessionId });
  }

  /**
   * Envia um comando livre a um agente (mia | max | sarah | alex).
   * O agente interpreta o comando e retorna os campos atualizados da estratégia.
   */
  async sendAgentCommand(
    agentId: "mia" | "max" | "sarah" | "alex",
    command: string,
    sessionId?: number,
    modelName?: string,
  ): Promise<{
    success: boolean;
    agent: string;
    updates: Partial<StrategyData>;
  }> {
    return await apiService.post("/api/agent/ads/strategy/agent-command", {
      agent_id: agentId,
      command,
      session_id: sessionId,
      model_name: modelName,
    });
  }
}

const adsStrategyService = new AdsStrategyService();
export default adsStrategyService;
